import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Collapse,
} from '@mui/material';
import { api, userActivityAPI } from '../services/api';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  Folder as ProjectIcon,
  Assignment as CaseIcon,
  AccountCircle,
  Logout,
  Approval as ApprovalIcon,
  Assessment as AssessmentIcon,
  Receipt as ReceiptIcon,
  AccountBalance as TdsIcon,
  Settings,
  Store as VendorIcon,
  ExpandLess,
  ExpandMore,
  BarChart as ReportsIcon,
  Schedule as TimeIcon,
  Email,
  Storage,
  History,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useScreenTracking } from '../hooks/useScreenTracking';
import { useActiveTimer } from '../hooks/useActiveTimer';
import { useInactivityDetector } from '../hooks/useInactivityDetector';
import { caseService } from '../services/caseService';
import InactivityNudgePopup from './InactivityNudgePopup';
import GavelIcon from '@mui/icons-material/Gavel';
import PolicyIcon from '@mui/icons-material/Policy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MasqueradeIndicator from './MasqueradeIndicator';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import EventNoteIcon from '@mui/icons-material/EventNote';

const drawerWidth = 240;

const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [vendorMenuOpen, setVendorMenuOpen] = useState(false);
  const [clientMenuOpen, setClientMenuOpen] = useState(false);
  const [reportsMenuOpen, setReportsMenuOpen] = useState(false);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const [logsMenuOpen, setLogsMenuOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const logoBlobUrlRef = useRef<string | null>(null);
  const { user, masquerade, logout, refreshUserData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { timerData, refetch: refetchTimer } = useActiveTimer();
  const [showInactivityNudge, setShowInactivityNudge] = useState(false);
  const [pauseTimerLoading, setPauseTimerLoading] = useState(false);

  // Only nudge when user is on this tab and idle; timer keeps running if they work in other tabs
  const { resetActivity } = useInactivityDetector({
    enabled: !!timerData?.has_checkin,
    inactivityMinutes: 1,
    onInactive: () => {
      setShowInactivityNudge(true);
      userActivityAPI.logInactivityNudge('shown').catch(() => {});
    },
    requireTabVisible: true,
  });

  const handleInactivityContinue = () => {
    userActivityAPI.logInactivityNudge('continue').catch(() => {});
    resetActivity();
    setShowInactivityNudge(false);
  };

  const handleInactivityPauseTimer = async () => {
    const caseId = timerData?.running_case_id;
    if (caseId == null) return;
    setPauseTimerLoading(true);
    try {
      await caseService.stopTimer(caseId);
      await refetchTimer();
      userActivityAPI.logInactivityNudge('pause_timer', {
        case_id: caseId,
        firm_id: timerData?.running_case_firm_id ?? undefined,
      }).catch(() => {});
      resetActivity();
      setShowInactivityNudge(false);
      const firmId = timerData?.running_case_firm_id;
      if (firmId) {
        navigate(`/firms/${firmId}/cases/${caseId}`);
      }
    } catch (err: any) {
      console.error('Failed to pause timer:', err);
    } finally {
      setPauseTimerLoading(false);
    }
  };

  // Track screen views
  useScreenTracking();

  // Handle masquerade redirect after page reload
  useEffect(() => {
    const redirectPath = sessionStorage.getItem('masqueradeRedirect');
    if (redirectPath) {
      sessionStorage.removeItem('masqueradeRedirect');
      navigate(redirectPath, { replace: true });
    }
  }, [navigate]);

  // Helper methods for user roles
  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'pmp_admin');
  };

  const isSiteAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'site_admin');
  };

  const isPmpAdminOrSiteAdmin = () => {
    return isPmpAdmin() || isSiteAdmin();
  };
  
  const isFirmAdmin = () => {
    if (!user?.roles) return false;
    const firmAdminRoleSlugs = ['client_admin', 'vendor_admin'];
    return user.roles.some(role => firmAdminRoleSlugs.includes(role.slug));
  };

  const isVendorUser = () => {
    if (!user?.roles) return false;
    const vendorRoleSlugs = ['vendor_staff', 'vendor_admin'];
    return user.roles.some(role => vendorRoleSlugs.includes(role.slug));
  };

  const isClientUser = () => {
    if (!user?.roles) return false;
    const clientRoleSlugs = ['firm_admin', 'firm_main', 'firm_staff'];
    return user.roles.some(role => clientRoleSlugs.includes(role.slug));
  };

  const isInternalStaff = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => ['internal_staff', 'legal_user', 'voice_user'].includes(role.slug));
  };

  const isClientSupervisor = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'client_supervisor');
  };

  const showSidebar = isPmpAdminOrSiteAdmin() || isVendorUser() || isClientUser() || isInternalStaff() || isClientSupervisor();

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    ...(isPmpAdminOrSiteAdmin() ? [
      { text: 'Firms', icon: <BusinessIcon />, path: '/firms' },
      { text: 'Case Tracker', icon: <CaseIcon />, path: '/cases' },
      ...(isPmpAdmin() ? [
        { text: 'Firm Approval', icon: <ApprovalIcon />, path: '/firm-approval' },
      ] : []),
      // { text: 'Projects', icon: <ProjectIcon />, path: '/projects' },
      ...(isPmpAdmin() ? [
        { text: 'Agreements', icon: <GavelIcon />, path: '/admin/agreements' },
        { text: 'User Management', icon: <Settings />, path: '/user-management' },
        { text: 'Legal Section', icon: <PolicyIcon />, path: '/legal-section' },
        { text: 'Masquerade Users', icon: <AccountCircle />, path: '/masquerade-users' },
      ] : []),
    ] : []),
    ...(isVendorUser() ? [
      { text: 'Firms', icon: <BusinessIcon />, path: '/firms' },
      { text: 'My Invoices', icon: <ReceiptIcon />, path: '/my-invoices' },
    ] : []),
  ];

  const vendorMenuItems = isPmpAdminOrSiteAdmin() ? [
    { text: 'Vendor Estimates', icon: <AssessmentIcon />, path: '/pending-estimates' },
    { text: 'Vendor Invoices', icon: <ReceiptIcon />, path: '/vendor-invoices' },
    { text: 'TDS Payments', icon: <TdsIcon />, path: '/tds-payments' },
  ] : [];

  const settingsMenuItems = isPmpAdmin() ? [
    { text: 'Email Settings', icon: <Email />, path: '/email-settings' },
    { text: 'System Settings', icon: <Settings />, path: '/system-settings' },
    { text: 'Timer Configuration', icon: <AccessTimeIcon />, path: '/timer-configuration' },
  ] : [];

  const clientMenuItems = [
    ...(isPmpAdminOrSiteAdmin() ? [
      { text: 'Client Invoices', icon: <ReceiptIcon />, path: '/client-invoices' },
    ] : []),
    ...((isPmpAdminOrSiteAdmin() || isClientUser()) ? [
      { text: 'Pending Estimates', icon: <AssessmentIcon />, path: '/pending-client-estimates' },
    ] : []),
  ];

  const hasClientManagerRole = () => {
    const roles = (user as any)?.roles || [];
    return roles.some((r: any) => ['client_manager', 'internal_manager'].includes(r.slug));
  };

  const canAccessReports = () => {
    return isPmpAdminOrSiteAdmin() || hasClientManagerRole() || isInternalStaff();
  };

  const canAccessAllReports = () => {
    return isPmpAdminOrSiteAdmin() || hasClientManagerRole();
  };

  const reportsMenuItems = canAccessReports() ? [
    { text: 'User Time Report', icon: <TimeIcon />, path: '/reports/user-time' },
    { text: 'User Checkin Report', icon: <TimeIcon />, path: '/reports/user-checkin' },
    ...(canAccessAllReports() ? [
      { text: 'Time Tracker Report', icon: <TimeIcon />, path: '/reports/time-tracker' },
      { text: 'Active Timer Report', icon: <AccessTimeIcon />, path: '/reports/active-timers' },
      { text: 'Idle Time Activity Report', icon: <AccessTimeIcon />, path: '/reports/idle-time-activity' },
    ] : []),
    ...(isPmpAdminOrSiteAdmin() ? [
      { text: 'Vendor Assignment Report', icon: <VendorIcon />, path: '/reports/vendor-assignment' },
      { text: 'Daily Assignment Report', icon: <EventNoteIcon />, path: '/reports/daily-assignment' },
    ] : []),
  ] : [];

  const logsMenuItems = isPmpAdmin() ? [
    { text: 'Email Logs', icon: <Email />, path: '/email-logs' },
    { text: 'Backend Logs', icon: <Storage />, path: '/backend-logs' },
    { text: 'User Activity Logs', icon: <History />, path: '/user-activity-logs' },
  ] : [];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleVendorMenuToggle = () => {
    setVendorMenuOpen(!vendorMenuOpen);
  };

  const handleClientMenuToggle = () => {
    setClientMenuOpen(!clientMenuOpen);
  };

  const handleReportsMenuToggle = () => {
    setReportsMenuOpen(!reportsMenuOpen);
  };

  const handleSettingsMenuToggle = () => {
    setSettingsMenuOpen(!settingsMenuOpen);
  };

  const handleLogsMenuToggle = () => {
    setLogsMenuOpen(!logsMenuOpen);
  };

  // Check if any vendor submenu item is currently selected
  const isVendorPathSelected = () => {
    return vendorMenuItems.some(item => location.pathname === item.path);
  };

  // Check if any client submenu item is currently selected
  const isClientPathSelected = () => {
    return clientMenuItems.some(item => location.pathname === item.path || location.pathname.startsWith(item.path));
  };

  // Check if any reports submenu item is currently selected
  const isReportsPathSelected = () => {
    return reportsMenuItems.some(item => location.pathname.startsWith(item.path));
  };

  const isSettingsPathSelected = () => {
    return settingsMenuItems.some(item => location.pathname === item.path);
  };

  // Check if any logs submenu item is currently selected
  const isLogsPathSelected = () => {
    return logsMenuItems.some(item => location.pathname === item.path);
  };

  // Auto-expand menus if user is on those pages
  useEffect(() => {
    if (isVendorPathSelected()) {
      setVendorMenuOpen(true);
    }
    if (isClientPathSelected()) {
      setClientMenuOpen(true);
    }
    if (isReportsPathSelected()) {
      setReportsMenuOpen(true);
    }
    if (isSettingsPathSelected()) {
      setSettingsMenuOpen(true);
    }
    if (isLogsPathSelected()) {
      setLogsMenuOpen(true);
    }
  }, [location.pathname]);

  // Fetch logo on mount
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await api.get('/system-settings');
        if (response.data.success && response.data.data.logo_url) {
          // Fetch the logo as a blob to include auth headers
          const logoResponse = await api.get(response.data.data.logo_url, {
            responseType: 'blob',
          });
          // Create object URL from blob
          const blobUrl = URL.createObjectURL(logoResponse.data);
          logoBlobUrlRef.current = blobUrl;
          setLogoUrl(blobUrl);
        }
      } catch (error) {
        // Silently fail - logo is optional
        console.error('Failed to fetch logo:', error);
      }
    };
    
    fetchLogo();
    
    // Cleanup: revoke object URL on unmount
    return () => {
      if (logoBlobUrlRef.current) {
        URL.revokeObjectURL(logoBlobUrlRef.current);
        logoBlobUrlRef.current = null;
      }
    };
  }, []);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleMenuClose();
  };

  const drawer = (
    <div>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', py: 1 }}>
          {logoUrl ? (
            <Box
              component="img"
              src={logoUrl}
              alt="Logo"
              sx={{
                maxHeight: 40,
                maxWidth: '70%',
                objectFit: 'contain',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/')}
            />
          ) : (
            <Typography variant="h6" noWrap component="div" sx={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
              PMP
            </Typography>
          )}
        </Box>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
        
        {/* Vendor Menu with Submenu - only show for PMP Admins */}
        {isPmpAdminOrSiteAdmin() && (
          <>
            <ListItem disablePadding>
              <ListItemButton
                selected={isVendorPathSelected()}
                onClick={handleVendorMenuToggle}
              >
                <ListItemIcon>
                  <VendorIcon />
                </ListItemIcon>
                <ListItemText primary="Vendor" />
                {vendorMenuOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={vendorMenuOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {vendorMenuItems.map((item) => (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton
                      sx={{ pl: 4 }}
                      selected={location.pathname === item.path}
                      onClick={() => navigate(item.path)}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}

        {/* Client Menu with Submenu - show for PMP Admins and Client Firm Users */}
        {(isPmpAdminOrSiteAdmin() || isClientUser()) && clientMenuItems.length > 0 && (
          <>
            <ListItem disablePadding>
              <ListItemButton
                selected={isClientPathSelected()}
                onClick={handleClientMenuToggle}
              >
                <ListItemIcon>
                  <BusinessIcon />
                </ListItemIcon>
                <ListItemText primary="Client" />
                {clientMenuOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={clientMenuOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {clientMenuItems.map((item) => (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton
                      sx={{ pl: 4 }}
                      selected={location.pathname === item.path || location.pathname.startsWith(item.path)}
                      onClick={() => navigate(item.path)}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}

        {/* Reports Menu with Submenu - show for PMP Admins, Client Managers, and Internal Staff */}
        {canAccessReports() && reportsMenuItems.length > 0 && (
          <>
            <ListItem disablePadding>
              <ListItemButton
                selected={isReportsPathSelected()}
                onClick={handleReportsMenuToggle}
              >
                <ListItemIcon>
                  <ReportsIcon />
                </ListItemIcon>
                <ListItemText primary="Reports" />
                {reportsMenuOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={reportsMenuOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {reportsMenuItems.map((item) => (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton
                      sx={{ pl: 4 }}
                      selected={location.pathname === item.path}
                      onClick={() => navigate(item.path)}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}

        {/* Settings Menu with Submenu - only show for PMP Admins */}
        {isPmpAdmin() && settingsMenuItems.length > 0 && (
          <>
            <ListItem disablePadding>
              <ListItemButton
                selected={isSettingsPathSelected()}
                onClick={handleSettingsMenuToggle}
              >
                <ListItemIcon>
                  <Settings />
                </ListItemIcon>
                <ListItemText primary="Settings" />
                {settingsMenuOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={settingsMenuOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {settingsMenuItems.map((item) => (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton
                      sx={{ pl: 4 }}
                      selected={location.pathname === item.path}
                      onClick={() => navigate(item.path)}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}

        {/* Logs Menu with Submenu - only show for PMP Admins */}
        {isPmpAdmin() && (
          <>
            <ListItem disablePadding>
              <ListItemButton
                selected={isLogsPathSelected()}
                onClick={handleLogsMenuToggle}
              >
                <ListItemIcon>
                  <Storage />
                </ListItemIcon>
                <ListItemText primary="Logs" />
                {logsMenuOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={logsMenuOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {logsMenuItems.map((item) => (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton
                      sx={{ pl: 4 }}
                      selected={location.pathname === item.path}
                      onClick={() => navigate(item.path)}
                    >
                      <ListItemIcon>{item.icon}</ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </>
        )}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: showSidebar ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { sm: showSidebar ? `${drawerWidth}px` : 0 },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              display: { xs: 'none', sm: 'block' }
            }}
          >
            Project Management Platform
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 2 } }}>
            {/* Active Timer Display */}
            {timerData?.has_checkin && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: { xs: 1, sm: 1.5 },
                  py: { xs: 0.5, sm: 0.75 },
                  borderRadius: 1,
                  bgcolor: 'rgba(255, 255, 255, 0.15)',
                  mr: { xs: 0.5, sm: 1 },
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                }}
              >
                <AccessTimeIcon sx={{ fontSize: { xs: 16, sm: 18 }, opacity: 0.9 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: { xs: '0.65rem', sm: '0.7rem' },
                      opacity: 0.8,
                      lineHeight: 1,
                      mb: 0.25,
                    }}
                  >
                    Active
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: { xs: '0.8rem', sm: '0.9rem' },
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    {timerData.formatted_time}
                  </Typography>
                </Box>
              </Box>
            )}
            <Typography 
              variant="body2" 
              sx={{ 
                mr: { xs: 1, sm: 2 },
                display: { xs: 'none', sm: 'block' },
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }}
            >
              {user?.name}
            </Typography>
            <IconButton
              size="large"
              aria-label="help and support"
              color="inherit"
              onClick={() => navigate('/help-support')}
              title="Help & Support"
            >
              <HelpOutlineIcon />
            </IconButton>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              {/* Check-in Timer Display with Pause / Stop */}
              {timerData?.has_checkin && (
                <>
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      minWidth: 200,
                    }}
                  >
                    <AccessTimeIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          color: 'text.secondary',
                          fontSize: '0.7rem',
                          mb: 0.25,
                        }}
                      >
                        Active Time Today
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.95rem',
                          fontWeight: 600,
                          color: 'primary.main',
                        }}
                      >
                        {timerData.formatted_time}
                      </Typography>
                    </Box>
                  </Box>
                  {timerData?.running_case_id != null && (
                    <MenuItem
                      onClick={() => {
                        handleMenuClose();
                        handleInactivityPauseTimer();
                      }}
                      disabled={pauseTimerLoading}
                    >
                      <ListItemIcon>
                        <PauseCircleOutlineIcon fontSize="small" />
                      </ListItemIcon>
                      Pause timer
                    </MenuItem>
                  )}
                  <Divider />
                </>
              )}
              <MenuItem onClick={() => { handleMenuClose(); navigate('/profile/edit'); }}>
                <ListItemIcon>
                  <Settings fontSize="small" />
                </ListItemIcon>
                Edit Profile
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      {showSidebar && (
        <Box
          component="nav"
          sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
          aria-label="mailbox folders"
        >
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
      )}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 3 },
          width: { sm: showSidebar ? `calc(100% - ${drawerWidth}px)` : '100%' },
          minWidth: 0, // Prevent overflow
        }}
      >
        <Toolbar />
        {masquerade && (
          <MasqueradeIndicator 
            masqueradeData={masquerade} 
            onStopMasquerade={refreshUserData}
          />
        )}
        <Outlet />
      </Box>
      <InactivityNudgePopup
        open={showInactivityNudge}
        onContinue={handleInactivityContinue}
        onPauseTimer={timerData?.running_case_id != null ? handleInactivityPauseTimer : undefined}
        runningCaseId={timerData?.running_case_id}
        pauseLoading={pauseTimerLoading}
      />
    </Box>
  );
};

export default Layout; 